import React, { useEffect, useState } from "react";
import { View, Pressable, TextInput } from "react-native-web";
import CustomText from "../atoms/CustomText";
import { colors, spacing } from "../../utils/styling";
import { useResponsive } from "../../hooks/useResponsive";
import ResultsGraph from "../common/ResultsGraph";
import CatContributionBar from "../common/CatContributionBar";
import { resolveCoreSection } from "../../utils/quizinfo/personalityContent";
import { useQuiz } from "../../lib/quizzes";
import BaseModal from './BaseModal';
import { QUESTIONS } from "../../utils/quizinfo/questions";
import SvgClose from "../icons/SvgClose";
import { migrateDatabaseQuizResults } from "../../utils/categoryMigration";
import { Lead } from "../../lib/leads";
import { useHtClient } from "../../providers/ht";

const CATEGORY_COLORS: Record<string, string> = {
  "Dream Chaser": "#00A4EF",
  "Wealth Architect": "#00B3C2",
  "Safety Seeker": "#076652",
  "Generous Altruist": "#9933FF",
  "Freedom Finder": "#FF00FF",
};

const SPECIFIC_CATEGORY_COLORS: Record<string, string> = {
  "Visionary Investor": "#00CFEF",
  "Powerhouse Investor": "#0074EF",
  "Cautious Saver": "#77FF92",
  "Responsible Saver": "#00AE23",
  "Analytic Investor": "#7BCCD2",
  "Ambitious Builder": "#00757F",
  "Purpose-Driven Provider": "#BB77FF",
  "Empathetic Giver": "#6510B9",
  "Lifestyle Spender": "#FF8AFF",
  "Spontaneous Spender": "#D600D6",
};

type ResultsType = Record<string, number>;

function getDominantCategory(results: ResultsType): string {
  if (!results || Object.entries(results).length === 0) {
    return "Freedom Finder";
  }
  return Object.entries(results).reduce((a, b) => (a[1] > b[1] ? a : b))[0];
}

interface LeadQuizResultsModalProps {
  lead: Lead | null;
  isVisible: boolean;
  onClose: () => void;
  onDelete?: () => void; // Callback to refresh data after deletion
}

export default function LeadQuizResultsModal({ lead, isVisible, onClose, onDelete }: LeadQuizResultsModalProps) {
  const { isMobile } = useResponsive();
  const htClient = useHtClient();
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Only fetch quiz data if we have a quiz ID
  const shouldFetchQuiz = !!(lead?.quiz && isVisible);
  const { data: quizData, isLoading, error } = useQuiz(shouldFetchQuiz ? lead!.quiz! : 0);

  // Reset confirmation state when modal closes
  React.useEffect(() => {
    if (!isVisible) {
      setShowDeleteConfirm(false);
      setDeleteConfirmText("");
      setIsDeleting(false);
    }
  }, [isVisible]);

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!lead || !quizData || !htClient.data) return;
    
    try {
      setIsDeleting(true);
      
      // Get the profile ID from the quiz data
      const profileId = quizData.profile;
      
      if (!profileId) {
        alert("No profile found for this user. Cannot delete.");
        return;
      }

      // Call the delete endpoint
      const response = await htClient.data.delete(`/leads/profile/${profileId}/complete?confirm=true`);
      
      if (response.status === 200) {
        alert("User and all associated data deleted successfully!");
        onClose();
        if (onDelete) {
          onDelete(); // Trigger data refresh in parent
        }
      } else {
        throw new Error("Delete failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete user. Please try again.");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmText("");
    }
  };

  // Handle delete button click
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = () => {
    if (deleteConfirmText === "DELETE") {
      handleDeleteUser();
    }
  };

  if (!isVisible || !lead) {
    return null;
  }

  // Show loading state only if we're actually fetching
  if (shouldFetchQuiz && isLoading) {
    return (
      <BaseModal isVisible={isVisible} onClose={onClose} title="">
        <View style={{
          padding: 40,
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200
        }}>
          <CustomText style={{
            fontSize: 18,
            color: colors.gray[600],
            fontFamily: 'Nunito'
          }}>
            Loading quiz results...
          </CustomText>
        </View>
      </BaseModal>
    );
  }

  // Show error or no quiz state
  if (!shouldFetchQuiz || error || !quizData) {
    return (
      <BaseModal isVisible={isVisible} onClose={onClose} title="">
        <View style={{
          padding: 40,
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200
        }}>
          <CustomText style={{
            fontSize: 18,
            color: colors.gray[600],
            fontFamily: 'Nunito',
            textAlign: 'center'
          }}>
            {!lead.quiz ? 'No quiz data available for this lead.' : 'Unable to load quiz results.'}
          </CustomText>
        </View>
      </BaseModal>
    );
  }

  // Apply migration to database quiz results
  let activeQuiz = migrateDatabaseQuizResults(quizData);
  
  const quizAnswers = Array.isArray(activeQuiz.answers?.quiz) ? activeQuiz.answers.quiz : [];
  const mainResults = activeQuiz?.results?.mainQuiz || {};
  const dominantCategory = getDominantCategory(mainResults);
  const categoryScores = mainResults;

  // Core-personality content, resolved defensively (ADR 2026-07-20). This
  // component indexed SPECIFIC_CATEGORY_SECTIONS[...] bare in seven places;
  // after the 2026-08-20 sub-quiz removal (ClickUp 86bb0f9vz) every new result
  // has an empty sub-type, so each one would have thrown on first render.
  const coreSection = resolveCoreSection(dominantCategory, "LeadQuizResultsModal");
  
  // Get user info from quiz data
  const userInfo: Array<{question: string; answer: string; question_id: number}> = 
    Array.isArray(activeQuiz.info) ? activeQuiz.info : [];

  // All possible main categories - ensure all are included even if 0%
  const ALL_MAIN_CATEGORIES = Object.keys(CATEGORY_COLORS);
  const completeMainCategoryScores = ALL_MAIN_CATEGORIES.reduce((acc, category) => {
    acc[category] = mainResults[category] ?? 0;
    return acc;
  }, {} as Record<string, number>);

  // Sort categories by percentage (descending)
  const sortedCategories = ALL_MAIN_CATEGORIES.sort(
    (a, b) => (completeMainCategoryScores[b] ?? 0) - (completeMainCategoryScores[a] ?? 0)
  );

  return (
    // On a phone, drop BaseModal's padded frame (its 400px minimum and second close
    // button pushed the card past the screen edge) and fill 90% of the width.
    <BaseModal isVisible={isVisible} onClose={onClose} title="" hideHeader={isMobile} width={isMobile ? 300 : undefined}>
      <View style={{
        width: isMobile ? '90vw' : 1000,
        maxHeight: '90vh',
        backgroundColor: colors.white,
        borderRadius: 16,
        overflow: 'hidden'
      }}>
        {/* Header */}
        <View style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 24,
          borderBottom: `2px solid ${colors.gray[200]}`,
          backgroundColor: colors.white
        }}>
          <View>
            <CustomText style={{
              fontSize: 24,
              fontWeight: 'bold',
              color: colors.dark,
              fontFamily: 'Nunito',
            }}>
              {lead.first_name} {lead.last_name}
            </CustomText>
            <CustomText style={{
              fontSize: 14,
              color: colors.gray[600],
              fontFamily: 'Nunito',
            }}>
              {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </CustomText>
          </View>
          
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            {/* Delete Button */}
            <Pressable 
              onPress={handleDeleteClick}
              disabled={!htClient.data}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: htClient.data 
                  ? (hovered ? '#dc2626' : '#ef4444')
                  : colors.gray[300],
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 6,
                cursor: htClient.data ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                opacity: htClient.data ? 1 : 0.6,
              })}
            >
              <CustomText style={{
                color: htClient.data ? colors.white : colors.gray[500],
                fontSize: 14,
                fontWeight: '600',
                fontFamily: 'Nunito',
              }}>
                Delete User
              </CustomText>
            </Pressable>
            
            {/* Close Button */}
            <Pressable onPress={onClose} style={{ padding: 8 }}>
              <SvgClose width={24} height={24} color={colors.gray[600]} />
            </Pressable>
          </View>
        </View>

        {/* Scrollable Content */}
        <View style={{
          flex: 1,
          overflowY: 'auto',
          padding: isMobile ? 12 : 24
        }}>
          {/* Header Card - "You are primarily a..." */}
          <View style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            padding: 24,
            marginBottom: 24,
            alignItems: "center",
            border: "3px solid black",
          }}>
            <CustomText style={{
              color: colors.dark,
              textAlign: "center",
              width: "100%",
              fontFamily: "Nunito",
              fontWeight: 400,
              fontSize: 24,
            }}>
              You are primarily a
              {dominantCategory.toLowerCase().startsWith("a") ||
              dominantCategory.toLowerCase().startsWith("e") ||
              dominantCategory.toLowerCase().startsWith("i") ||
              dominantCategory.toLowerCase().startsWith("o") ||
              dominantCategory.toLowerCase().startsWith("u")
                ? "n "
                : " "}
            </CustomText>
            <CustomText style={{
              fontSize: isMobile ? 40 : 72,
              fontWeight: "700",
              color: colors.dark,
              textAlign: "center",
              width: "100%",
              fontFamily: "Nunito",
            }}>
              {dominantCategory}
            </CustomText>
            <CustomText style={{
              fontSize: 20,
              color: colors.gray[700],
              textAlign: "center",
              width: "100%",
              marginBottom: 16,
              fontFamily: "Nunito",
            }}>
              {coreSection.Subtext}
            </CustomText>
            <CustomText style={{
              fontSize: 16,
              color: colors.gray[700],
              lineHeight: 24,
              fontFamily: "Nunito",
              textAlign: "center",
              width: "90%",
            }}>
              {coreSection.Overview}
            </CustomText>
          </View>

          {/* Graph Card */}
          <View style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            padding: isMobile ? 16 : 24,
            marginBottom: 24,
            width: "100%",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-around",
            border: "3px solid black",
          }}>
            {/* Each category and their relative contributions */}
            <View style={{
              width: isMobile ? "100%" : "50%",
              padding: isMobile ? 0 : 24,
            }}>
              {sortedCategories.map((category) => (
                <View key={category} style={{ marginBottom: 24 }}>
                  <View style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: 4,
                  }}>
                    <CustomText style={{ fontFamily: "Nunito", fontSize: 18 }}>
                      {category}
                    </CustomText>
                    <CustomText style={{ color: "#717171", fontWeight: 400, fontSize: 16 }}>
                      {Math.round(completeMainCategoryScores[category])}%
                    </CustomText>
                  </View>
                  <CatContributionBar
                    value={Math.round(completeMainCategoryScores[category])}
                    color={CATEGORY_COLORS[category] || colors.primary}
                  />
                </View>
              ))}
            </View>
            {/* The pie chart graph of all categories */}
            <View style={{
              width: isMobile ? "100%" : "50%",
              justifyContent: "center",
            }}>
              <ResultsGraph graphData={completeMainCategoryScores} type="main" />
            </View>
          </View>

          {/* Traits Section */}
          <View style={{
            width: "100%",
            marginBottom: 24,
            backgroundColor: colors.white,
            borderRadius: 16,
            padding: 24,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            justifyContent: "space-between",
            border: "3px solid black",
          }}>
            <View style={{ flex: 1, minWidth: 0, width: "100%" }}>
              <View style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: spacing.sm,
                marginBottom: 16,
              }}>
                <CustomText style={{
                  fontSize: 32,
                  fontWeight: "700",
                  color: colors.dark,
                  fontFamily: "Nunito",
                  textAlign: "center",
                }}>
                  Dominant traits
                </CustomText>
              </View>
              <View style={{
                display: "flex",
                flexDirection: "column",
                flexWrap: "wrap",
                alignItems: "flex-start",
                gap: spacing.sm,
                marginBottom: 16,
                width: "100%",
              }}>
                {coreSection.DominantTraits.map(
                  (trait, i) => (
                    <View
                      key={i}
                      style={{
                        backgroundColor: colors.gray[100],
                        paddingHorizontal: spacing.sm,
                        paddingVertical: spacing.xs,
                        borderRadius: 4,
                        marginBottom: spacing.xs,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      <CustomText style={{
                        fontSize: 16,
                        color: colors.gray[700],
                        fontFamily: "Nunito",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}>
                        {trait}
                      </CustomText>
                    </View>
                  )
                )}
              </View>
            </View>
          </View>

          {/* Strengths and Weaknesses */}
          <View style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "stretch",
            width: "100%",
            gap: 16,
            marginBottom: 24,
          }}>
            {/* Strengths Section */}
            <View style={{
              flex: 1,
              backgroundColor: "#ebf7ed",
              borderRadius: 16,
              padding: 24,
              display: "flex",
              flexDirection: "column",
              border: "3px solid #b1ddb4",
            }}>
              <CustomText style={{
                fontSize: 32,
                fontWeight: "700",
                color: colors.dark,
                fontFamily: "Nunito",
                marginBottom: 16,
              }}>
                ✅ Strengths
              </CustomText>
              <View style={{
                display: "flex",
                flexDirection: "column",
                gap: spacing.sm,
                alignItems: "flex-start",
                width: "100%",
              }}>
                {coreSection.Strengths.map(
                  (strength, i) => (
                    <View
                      key={i}
                      style={{
                        paddingHorizontal: spacing.sm,
                        paddingVertical: spacing.xs,
                        borderRadius: 4,
                        marginBottom: spacing.xs,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 10,
                        color: colors.success,
                      }}
                    >
                      <CustomText style={{
                        alignSelf: "flex-start",
                        paddingTop: 5,
                        fontSize: 20,
                        color: colors.success,
                      }}>
                        &#x2713;
                      </CustomText>
                      <CustomText style={{
                        fontSize: 16,
                        color: colors.success,
                        fontFamily: "Nunito",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}>
                        {strength}
                      </CustomText>
                    </View>
                  )
                )}
              </View>
            </View>

            {/* Weaknesses Section */}
            <View style={{
              flex: 1,
              backgroundColor: "#fffbe8",
              borderRadius: 16,
              padding: 24,
              display: "flex",
              flexDirection: "column",
              border: "3px solid #f4e67d",
            }}>
              <CustomText style={{
                fontSize: 32,
                fontWeight: "700",
                color: colors.dark,
                fontFamily: "Nunito",
                marginBottom: 16,
              }}>
                ⚠️ Weaknesses
              </CustomText>
              <View style={{
                display: "flex",
                flexDirection: "column",
                gap: spacing.sm,
                alignItems: "flex-start",
                width: "100%",
              }}>
                {coreSection.Weaknesses.map(
                  (weakness, i) => (
                    <View
                      key={i}
                      style={{
                        paddingHorizontal: spacing.sm,
                        paddingVertical: spacing.xs,
                        borderRadius: 4,
                        marginBottom: spacing.xs,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 10,
                        color: colors.danger,
                      }}
                    >
                      <View style={{ alignSelf: "flex-start", paddingTop: 5 }}>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 15 15"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.4449 0.608765C8.0183 -0.107015 6.9817 -0.107015 6.55509 0.608766L0.161178 11.3368C-0.275824 12.07 0.252503 13 1.10608 13H13.8939C14.7475 13 15.2758 12.07 14.8388 11.3368L8.4449 0.608765ZM7.4141 1.12073C7.45288 1.05566 7.54712 1.05566 7.5859 1.12073L13.9798 11.8488C14.0196 11.9154 13.9715 12 13.8939 12H1.10608C1.02849 12 0.980454 11.9154 1.02018 11.8488L7.4141 1.12073ZM6.8269 4.48611C6.81221 4.10423 7.11783 3.78663 7.5 3.78663C7.88217 3.78663 8.18778 4.10423 8.1731 4.48612L8.01921 8.48701C8.00848 8.766 7.7792 8.98664 7.5 8.98664C7.2208 8.98664 6.99151 8.766 6.98078 8.48701L6.8269 4.48611ZM8.24989 10.476C8.24989 10.8902 7.9141 11.226 7.49989 11.226C7.08567 11.226 6.74989 10.8902 6.74989 10.476C6.74989 10.0618 7.08567 9.72599 7.49989 9.72599C7.9141 9.72599 8.24989 10.0618 8.24989 10.476Z"
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                      </View>
                      <CustomText style={{
                        fontSize: 16,
                        color: colors.danger,
                        fontFamily: "Nunito",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}>
                        {weakness}
                      </CustomText>
                    </View>
                  )
                )}
              </View>
            </View>
          </View>

          {/* Sub-Personality */}
          <View style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "stretch",
            width: "100%",
            gap: 16,
            marginBottom: 24,
          }}>
            {/* Sub-Personality Section */}
            {/* The Subset Personality card (sub-type graph, contribution bars,
                sub-type definition) was REMOVED on 2026-08-20 — ClickUp
                86bb0f9vz. McKayla: "They don't need to know if they're a
                subtype." */}

          </View>

          {/* Growth Opportunities and Behavioral Concepts */}
          <View style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "stretch",
            width: "100%",
            gap: 16,
            marginBottom: 24,
          }}>
            {/* Growth Opportunities */}
            <View style={{
              flex: 1,
              backgroundColor: "white",
              borderRadius: 16,
              padding: 24,
              border: "3px solid black",
            }}>
              <CustomText style={{
                fontSize: 32,
                fontWeight: "700",
                color: colors.dark,
                fontFamily: "Nunito",
                marginBottom: 16,
              }}>
                🌱 Growth Opportunities
              </CustomText>
              <View style={{
                display: "flex",
                flexDirection: "column",
                gap: spacing.sm,
                alignItems: "flex-start",
                width: "100%",
              }}>
                {coreSection.GrowthOpportunities.map((opportunity, i) => (
                  <View
                    key={i}
                    style={{
                      backgroundColor: colors.green + "20",
                      paddingHorizontal: spacing.sm,
                      paddingVertical: spacing.xs,
                      borderRadius: 4,
                      marginBottom: spacing.xs,
                    }}
                  >
                    <CustomText style={{
                      fontSize: 16,
                      color: colors.green,
                      fontFamily: "Nunito",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}>
                      {opportunity}
                    </CustomText>
                  </View>
                ))}
              </View>
            </View>

            {/* Behavioral Concepts */}
            <View style={{
              flex: 1,
              backgroundColor: "white",
              borderRadius: 16,
              padding: 24,
              border: "3px solid black",
            }}>
              <CustomText style={{
                fontSize: 32,
                fontWeight: "700",
                color: colors.dark,
                fontFamily: "Nunito",
                marginBottom: 16,
              }}>
                🧠 Behavioral Concepts
              </CustomText>
              <View style={{
                display: "flex",
                flexDirection: "column",
                gap: spacing.sm,
                alignItems: "flex-start",
                width: "100%",
              }}>
                {coreSection.BehavioralConcepts.map((concept, i) => (
                  <View
                    key={i}
                    style={{
                      backgroundColor: colors.pink + "20",
                      paddingHorizontal: spacing.sm,
                      paddingVertical: spacing.xs,
                      borderRadius: 4,
                      marginBottom: spacing.xs,
                    }}
                  >
                    <CustomText style={{
                      fontSize: 16,
                      color: colors.pink,
                      fontFamily: "Nunito",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}>
                      {concept}
                    </CustomText>
                  </View>
                ))}
              </View>
            </View>
          </View>

          {/* Personal Information Section */}
          {userInfo.length > 0 && (
            <View style={{
              backgroundColor: '#f8f9fa',
              borderRadius: 16,
              padding: 24,
              marginBottom: 24,
              border: '2px solid #e9ecef',
            }}>
              <CustomText style={{
                fontSize: 24,
                fontWeight: 'bold',
                marginBottom: 16,
                color: colors.dark,
                fontFamily: 'Nunito',
                textAlign: 'center',
              }}>
                Personal Information
              </CustomText>
              <View style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 12,
              }}>
                {userInfo.map((item, index) => (
                  <View
                    key={index}
                    style={{
                      backgroundColor: colors.white,
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 8,
                      border: '1px solid #dee2e6',
                      width: '48%',
                    }}
                  >
                    <CustomText style={{
                      fontSize: 12,
                      fontWeight: 'bold',
                      marginBottom: 4,
                      color: colors.gray[600],
                      fontFamily: 'Nunito',
                    }}>
                      {item.question}
                    </CustomText>
                    <CustomText style={{
                      fontSize: 14,
                      color: colors.dark,
                      fontFamily: 'Nunito',
                      fontWeight: 'bold',
                    }}>
                      {item.answer}
                    </CustomText>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Quiz Answers Section */}
          <View style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            padding: 24,
            border: '2px solid #e9ecef',
          }}>
            <CustomText style={{
              fontSize: 24,
              fontWeight: 'bold',
              marginBottom: 16,
              color: colors.dark,
              fontFamily: 'Nunito',
            }}>
              Quiz Responses
            </CustomText>
            
            {/* Main Quiz Questions */}
            <CustomText style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: colors.dark,
              fontFamily: 'Nunito',
            }}>
              Primary Personality Questions
            </CustomText>
            
            {quizAnswers.slice(0, quizAnswers.length - 5).map((item: { question: string; answer: string; category: string }, idx: number) => (
              <View
                key={idx}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: 16,
                  padding: 12,
                  backgroundColor: colors.gray[100],
                  borderRadius: 8,
                  minHeight: 60,
                }}
              >
                <View style={{ flex: 1 }}>
                  <CustomText style={{
                    fontSize: 14,
                    fontWeight: 'bold',
                    marginBottom: 4,
                    color: colors.dark,
                    fontFamily: 'Nunito',
                  }}>
                    {item.question}
                  </CustomText>
                  <CustomText style={{
                    fontSize: 12,
                    color: colors.gray[700],
                    fontFamily: 'Nunito',
                  }}>
                    {item.answer}
                  </CustomText>
                </View>
                <View style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  minWidth: 100,
                  justifyContent: 'flex-end',
                  gap: 8,
                  marginTop: 8,
                }}>
                  <View style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: CATEGORY_COLORS[item.category] || colors.gray[400],
                    marginRight: 6,
                  }} />
                  <CustomText style={{
                    fontSize: 10,
                    color: colors.gray[500],
                    fontFamily: 'Nunito',
                  }}>
                    {item.category}
                  </CustomText>
                </View>
              </View>
            ))}

            {/* The "Subset Questions" list was REMOVED on 2026-08-20 (ClickUp
                86bb0f9vz). It rendered `quizAnswers.slice(-5)` — with the
                sub-quiz gone that slice returns the last five MAIN answers, so
                keeping it relabels real answers as sub-type ones. */}
          </View>
        </View>
        
        {/* Delete Confirmation Overlay */}
        {showDeleteConfirm && (
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}>
            <View style={{
              backgroundColor: colors.white,
              borderRadius: 16,
              padding: 32,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}>
              <CustomText style={{
                fontSize: 24,
                fontWeight: 'bold',
                color: colors.danger,
                fontFamily: 'Nunito',
                textAlign: 'center',
                marginBottom: 16,
              }}>
                ⚠️ Delete User Permanently
              </CustomText>
              
              <CustomText style={{
                fontSize: 16,
                color: colors.dark,
                fontFamily: 'Nunito',
                textAlign: 'center',
                marginBottom: 24,
                lineHeight: 24,
              }}>
                This will permanently delete <strong>{lead.first_name} {lead.last_name}</strong> and ALL associated data including:
              </CustomText>
              
              <View style={{
                backgroundColor: '#f9fafb',
                borderRadius: 8,
                padding: 16,
                marginBottom: 24,
              }}>
                <CustomText style={{
                  fontSize: 14,
                  color: colors.dark,
                  fontFamily: 'Nunito',
                  lineHeight: 20,
                }}>
                  • User profile and account{'\n'}
                  • Quiz results and responses{'\n'}
                  • Purchase history{'\n'}
                  • Subscription data{'\n'}
                  • PDF downloads{'\n'}
                  • Lead information
                </CustomText>
              </View>
              
              <CustomText style={{
                fontSize: 16,
                color: colors.danger,
                fontFamily: 'Nunito',
                textAlign: 'center',
                marginBottom: 16,
                fontWeight: 'bold',
              }}>
                This action CANNOT be undone!
              </CustomText>
              
              <CustomText style={{
                fontSize: 14,
                color: colors.dark,
                fontFamily: 'Nunito',
                marginBottom: 8,
              }}>
                Type "DELETE" to confirm:
              </CustomText>
              
              <TextInput
                value={deleteConfirmText}
                onChangeText={setDeleteConfirmText}
                placeholder="Type DELETE here"
                style={{
                  borderWidth: 2,
                  borderColor: colors.gray[300],
                  borderRadius: 6,
                  padding: 12,
                  fontSize: 16,
                  fontFamily: 'Nunito',
                  marginBottom: 24,
                  backgroundColor: colors.white,
                  outlineStyle: 'none',
                }}
                autoFocus
              />
              
              <View style={{
                flexDirection: 'row',
                gap: 12,
                justifyContent: 'center',
              }}>
                <Pressable
                  onPress={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText("");
                  }}
                  style={({ hovered }: { hovered: boolean }) => ({
                    backgroundColor: hovered ? colors.gray[200] : colors.gray[100],
                    paddingHorizontal: 24,
                    paddingVertical: 12,
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  })}
                >
                  <CustomText style={{
                    color: colors.dark,
                    fontSize: 16,
                    fontWeight: '600',
                    fontFamily: 'Nunito',
                  }}>
                    Cancel
                  </CustomText>
                </Pressable>
                
                <Pressable
                  onPress={handleConfirmDelete}
                  disabled={deleteConfirmText !== "DELETE" || isDeleting}
                  style={({ hovered }: { hovered: boolean }) => ({
                    backgroundColor: deleteConfirmText === "DELETE" && !isDeleting
                      ? (hovered ? '#dc2626' : '#ef4444')
                      : colors.gray[300],
                    paddingHorizontal: 24,
                    paddingVertical: 12,
                    borderRadius: 6,
                    cursor: deleteConfirmText === "DELETE" && !isDeleting ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    opacity: isDeleting ? 0.7 : 1,
                  })}
                >
                  <CustomText style={{
                    color: deleteConfirmText === "DELETE" && !isDeleting ? colors.white : colors.gray[500],
                    fontSize: 16,
                    fontWeight: '600',
                    fontFamily: 'Nunito',
                  }}>
                    {isDeleting ? 'Deleting...' : 'Delete Forever'}
                  </CustomText>
                </Pressable>
              </View>
            </View>
          </View>
        )}
      </View>
    </BaseModal>
  );
}